/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/DisaEntrySections/GeneralSection.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    General section component for DISA Entry form.
────────────────────────────────────────────────────────────────*/

import { PhoneCall } from 'lucide-react';
import { useI18n } from '../../../../hooks/useI18n';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import Toggle from '../../../../components/ui/Toggle';
import { SimpleTooltip } from '../../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../../components/ui/FormBox';
import SelectClassOfService from '../../../../components/ui/SelectClassOfService';
import { FormState, ValidationErrors, ClassOfServiceOption } from '../../types';

interface GeneralSectionProps {
  formData: FormState;
  errors: ValidationErrors;
  classOfServices: ClassOfServiceOption[];
  onFieldChange: (field: keyof FormState, value: string | number | boolean | null) => void;
}

export function GeneralSection({
  formData,
  errors,
  classOfServices,
  onFieldChange
}: GeneralSectionProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('disaEntries:sections.general')} icon={<PhoneCall className="h-5 w-5" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
        <Label htmlFor="name">
          <SimpleTooltip content={t('disaEntries:fields.name.tooltip')}>
            <span>
              {t('disaEntries:fields.name.label')}
              <span className="text-red-500 ml-1">*</span>
            </span>
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="name"
            value={formData.name}
            onChange={(event) => onFieldChange('name', event.target.value)}
            placeholder={t('disaEntries:fields.name.placeholder')}
            className={errors.name ? 'border-red-500' : ''}
          />
          {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
        </div>

        <Label htmlFor="password">
          <SimpleTooltip content={t('disaEntries:fields.password.tooltip')}>
            <span>
              {t('disaEntries:fields.password.label')}
              <span className="text-red-500 ml-1">*</span>
            </span>
          </SimpleTooltip>
        </Label>
        <div>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(event) => onFieldChange('password', event.target.value)}
            placeholder={t('disaEntries:fields.password.placeholder')}
            className={errors.password ? 'border-red-500' : ''}
          />
          {errors.password && <p className="text-sm text-red-500 mt-1">{errors.password}</p>}
        </div>

        <Label htmlFor="classOfServicesId">
          <SimpleTooltip content={t('disaEntries:fields.classOfService.tooltip')}>
            {t('disaEntries:fields.classOfService.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <SelectClassOfService
            value={formData.classOfServicesId}
            onChange={(value) => onFieldChange('classOfServicesId', value)}
            classOfServices={classOfServices}
            placeholder={t('disaEntries:fields.classOfService.placeholder')}
          />
        </div>

        <Label>
          <SimpleTooltip content={t('disaEntries:fields.enabled.tooltip')}>
            {t('disaEntries:fields.enabled.label')}
          </SimpleTooltip>
        </Label>
        <div>
          <Toggle value={formData.enabled} onChange={(value) => onFieldChange('enabled', value)} />
        </div>
      </div>
    </FormBox>
  );
}

